import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useApi } from '@/hooks/useApi'
import LegalDisclaimer from '@/components/LegalDisclaimer'

// ── Types ──────────────────────────────────────────────────────────────────

interface PriceComparisonItem {
  country_code: string
  currency: string
  price: number | null
  price_text: string
  error: string | null
}

interface URLCompareResponse {
  platform: string
  original_url: string
  hotel_name: string
  items: PriceComparisonItem[]
  errors: string[]
}

type SortField = 'price' | 'country_code'

// ── Helpers ────────────────────────────────────────────────────────────────

const PLATFORM_ICONS: Record<string, string> = {
  agoda: '🟠',
  booking: '🔵',
}

const COUNTRY_FLAGS: Record<string, string> = {
  US: '🇺🇸', JP: '🇯🇵', GB: '🇬🇧', DE: '🇩🇪', FR: '🇫🇷',
  AU: '🇦🇺', CA: '🇨🇦', SG: '🇸🇬', KR: '🇰🇷', IN: '🇮🇳',
  BR: '🇧🇷', CN: '🇨🇳', TW: '🇹🇼', HK: '🇭🇰', IT: '🇮🇹',
  ES: '🇪🇸', NL: '🇳🇱', SE: '🇸🇪', NO: '🇳🇴', DK: '🇩🇰',
  FI: '🇫🇮', CH: '🇨🇭', AT: '🇦🇹', BE: '🇧🇪', IE: '🇮🇪',
  NZ: '🇳🇿', MX: '🇲🇽', TH: '🇹🇭', MY: '🇲🇾', PH: '🇵🇭',
  ID: '🇮🇩', VN: '🇻🇳', ZA: '🇿🇦', AE: '🇦🇪', SA: '🇸🇦',
}

function formatCurrency(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price)
  } catch {
    return `${currency} ${price.toLocaleString()}`
  }
}

function getFlag(code: string): string {
  return COUNTRY_FLAGS[code] || '🌐'
}

// ── Country Selector ──────────────────────────────────────────────────────

const AVAILABLE_COUNTRIES = [
  { code: 'US', label: 'United States' },
  { code: 'JP', label: 'Japan' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'AU', label: 'Australia' },
  { code: 'CA', label: 'Canada' },
  { code: 'SG', label: 'Singapore' },
  { code: 'KR', label: 'South Korea' },
  { code: 'IN', label: 'India' },
  { code: 'BR', label: 'Brazil' },
  { code: 'TW', label: 'Taiwan' },
  { code: 'HK', label: 'Hong Kong' },
  { code: 'CN', label: 'China' },
  { code: 'IT', label: 'Italy' },
  { code: 'ES', label: 'Spain' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'SE', label: 'Sweden' },
  { code: 'NO', label: 'Norway' },
  { code: 'DK', label: 'Denmark' },
  { code: 'FI', label: 'Finland' },
  { code: 'CH', label: 'Switzerland' },
  { code: 'AT', label: 'Austria' },
  { code: 'BE', label: 'Belgium' },
  { code: 'IE', label: 'Ireland' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'MX', label: 'Mexico' },
  { code: 'TH', label: 'Thailand' },
  { code: 'MY', label: 'Malaysia' },
  { code: 'PH', label: 'Philippines' },
  { code: 'ID', label: 'Indonesia' },
  { code: 'VN', label: 'Vietnam' },
  { code: 'ZA', label: 'South Africa' },
  { code: 'AE', label: 'UAE' },
  { code: 'SA', label: 'Saudi Arabia' },
]

function CountrySelector({
  selected,
  onChange,
}: {
  selected: string[]
  onChange: (codes: string[]) => void
}) {
  const { t } = useTranslation()
  
  const toggle = (code: string) => {
    if (selected.includes(code)) {
      onChange(selected.filter(c => c !== code))
    } else {
      onChange([...selected, code])
    }
  }
  
  const selectAll = () => onChange(AVAILABLE_COUNTRIES.map(c => c.code))
  const clearAll = () => onChange([])
  const popular = ['US', 'JP', 'GB', 'DE', 'FR', 'AU', 'CA', 'SG', 'KR']
  
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={selectAll}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          {t('urlCompare.selectAll')}
        </button>
        <span className="text-gray-300">|</span>
        <button
          type="button"
          onClick={clearAll}
          className="text-xs text-red-600 hover:text-red-800 font-medium"
        >
          {t('urlCompare.clearAll')}
        </button>
      </div>
      
      {/* Popular countries */}
      <div>
        <p className="text-xs text-gray-500 mb-1">{t('urlCompare.popular')}</p>
        <div className="flex flex-wrap gap-1.5">
          {popular.map(code => (
            <button
              key={code}
              type="button"
              onClick={() => toggle(code)}
              className={`px-2 py-1 rounded text-sm transition border ${
                selected.includes(code)
                  ? 'bg-blue-100 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {getFlag(code)} {code}
            </button>
          ))}
        </div>
      </div>
      
      {/* All countries */}
      <details className="group">
        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 select-none">
          {t('urlCompare.allCountries')} ▸
        </summary>
        <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5">
          {AVAILABLE_COUNTRIES.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              onClick={() => toggle(code)}
              className={`px-2 py-1 rounded text-xs transition border truncate ${
                selected.includes(code)
                  ? 'bg-blue-100 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
              title={label}
            >
              {getFlag(code)} {code}
            </button>
          ))}
        </div>
      </details>
      
      <p className="text-xs text-gray-400">
        {selected.length} / {AVAILABLE_COUNTRIES.length} {t('urlCompare.countriesSelected')}
      </p>
    </div>
  )
}

// ── Results Table ─────────────────────────────────────────────────────────

function ResultsTable({
  data,
  sortField,
  onSort,
}: {
  data: URLCompareResponse | null
  sortField: SortField
  onSort: (field: SortField) => void
}) {
  const { t } = useTranslation()
  
  const sortedItems = useMemo(() => {
    if (!data) return []
    const priced = data.items.filter(i => i.price !== null).sort((a, b) => {
      const pa = a.price!
      const pb = b.price!
      return sortField === 'price' 
        ? (sortDir === 'asc' ? pa - pb : pb - pa)
        : a.country_code.localeCompare(b.country_code)
    })
    const failed = data.items.filter(i => i.price === null && i.error)
    return [...priced, ...failed]
  }, [data, sortField])

  // Find best price
  const bestPrice = useMemo(() => {
    if (!data) return null
    const priced = data.items.filter(i => i.price !== null)
    if (priced.length === 0) return null
    return priced.reduce((min, item) => item.price! < min.price! ? item : min)
  }, [data])

  if (!data) return null
  
  return (
    <div className="space-y-4">
      {/* Platform badge & hotel name */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{PLATFORM_ICONS[data.platform] || '🏨'}</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 uppercase tracking-wide">{data.platform}</p>
            <h3 className="font-semibold text-gray-900 truncate">{data.hotel_name}</h3>
          </div>
          <a
            href={data.original_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {t('urlCompare.viewOriginal')} →
          </a>
        </div>
      </div>

      {/* Best price banner */}
      {bestPrice && (
        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🏆</span>
            <div>
              <p className="text-sm text-green-700 font-medium">{t('urlCompare.bestPrice')}</p>
              <p className="text-2xl font-bold text-green-800">
                {formatCurrency(bestPrice.price!, bestPrice.currency)}
              </p>
              <p className="text-sm text-green-600">
                {getFlag(bestPrice.country_code)} {bestPrice.country_code} · {bestPrice.price_text}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sort controls */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">{t('urlCompare.sort')}:</span>
        <button
          onClick={() => onSort('price')}
          className={`text-sm px-3 py-1 rounded transition ${
            sortField === 'price'
              ? 'bg-blue-100 text-blue-700 font-medium'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {t('urlCompare.byPrice')} {sortField === 'price' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
        </button>
        <button
          onClick={() => onSort('country_code')}
          className={`text-sm px-3 py-1 rounded transition ${
            sortField === 'country_code'
              ? 'bg-blue-100 text-blue-700 font-medium'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {t('urlCompare.byCountry')} {sortField === 'country_code' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
        </button>
      </div>

      {/* Price cards */}
      <div className="grid gap-3">
        {sortedItems.map((item, index) => {
          const isBest = bestPrice?.country_code === item.country_code
          
          if (item.error) {
            // Failed entry
            return (
              <div
                key={item.country_code}
                className="bg-white rounded-lg shadow p-4 border border-red-200 flex items-center gap-4"
              >
                <span className="text-lg">{getFlag(item.country_code)}</span>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{item.country_code}</p>
                  <p className="text-sm text-red-600">{item.error}</p>
                </div>
                <span className="text-gray-400">❌</span>
              </div>
            )
          }
          
          return (
            <div
              key={item.country_code}
              className={`rounded-lg shadow p-4 flex items-center gap-4 transition ${
                isBest ? 'ring-2 ring-green-400 bg-green-50' : 'bg-white'
              }`}
            >
              {/* Rank badge */}
              <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-gray-100 text-gray-600">
                {index + 1}
              </div>

              {/* Country */}
              <div className="flex-shrink-0 text-2xl">{getFlag(item.country_code)}</div>

              {/* Hotel info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">{item.country_code}</p>
                <p className="text-sm text-gray-500">{item.price_text}</p>
              </div>

              {/* Price */}
              <div className="text-right flex-shrink-0">
                <p className={`text-xl font-bold ${isBest ? 'text-green-600' : 'text-blue-600'}`}>
                  {item.price !== null ? formatCurrency(item.price, item.currency) : '—'}
                </p>
                {isBest && (
                  <p className="text-xs text-green-600 font-medium">{t('urlCompare.lowest')}</p>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {sortedItems.length === 0 && data.items.length > 0 && (
        <p className="text-center text-gray-400 py-8">
          {t('urlCompare.noPricesFound')}
        </p>
      )}
    </div>
  )
}

let sortDir: 'asc' | 'desc' = 'asc'

// ── Main Page ──────────────────────────────────────────────────────────────

export default function SearchPage() {
  const { t } = useTranslation()
  const { api } = useApi()

  const [url, setUrl] = useState('')
  const [countries, setCountries] = useState<string[]>(['US', 'JP', 'GB', 'DE', 'FR'])
  const [loading, setLoading] = useState(false)
  const [compareData, setCompareData] = useState<URLCompareResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sortField, setSortField] = useState<SortField>('price')

  const handleSort = (field: SortField) => {
    setSortField(field)
    sortDir = sortDir === 'asc' ? 'desc' : 'asc'
  }

  const handleCompare = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!url.trim()) {
      setError(t('urlCompare.enterUrl'))
      return
    }
    if (countries.length === 0) {
      setError(t('urlCompare.selectCountries'))
      return
    }
    
    setLoading(true)
    setError(null)
    setCompareData(null)
    
    try {
      const res = await api.post<URLCompareResponse>('/prices/url-compare', {
        url: url.trim(),
        country_codes: countries,
      })
      setCompareData(res.data)
    } catch (err: unknown) {
      if (err instanceof Error) {
        if (err.message.includes('Pro tier required')) {
          setError(t('urlCompare.proRequired'))
        } else if (err.message.includes('400')) {
          setError(t('urlCompare.invalidUrl'))
        } else {
          setError(err.message || t('urlCompare.scrapeFailed'))
        }
      } else {
        setError(t('urlCompare.scrapeFailed'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('urlCompare.title')}
        </h1>
        <p className="text-gray-600">
          {t('urlCompare.subtitle')}
        </p>
      </div>

      {/* URL Input Form */}
      <form onSubmit={handleCompare} className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="space-y-4">
          {/* URL input */}
          <div>
            <label htmlFor="hotel-url" className="block text-sm font-medium text-gray-700 mb-1">
              {t('urlCompare.hotelUrl')}
            </label>
            <input
              id="hotel-url"
              type="url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="https://www.agoda.com/xxx/hotel-name.html"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none font-mono text-sm"
            />
          </div>

          {/* Country selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('urlCompare.compareFrom')}
            </label>
            <CountrySelector selected={countries} onChange={setCountries} />
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading || !url.trim()}
            className="w-full px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('urlCompare.comparing')}...
              </span>
            ) : (
              t('urlCompare.comparePrices')
            )}
          </button>
        </div>
      </form>

      {/* Results */}
      {compareData && (
        <ResultsTable
          data={compareData}
          sortField={sortField}
          onSort={handleSort}
        />
      )}

      {/* Legal disclaimer */}
      <LegalDisclaimer />
    </div>
  )
}
